import React, { useState } from "react";

import { useDispatch } from "react-redux";

import { faPencil, faPlus, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { Divider, Grid } from "@material-ui/core";

import { EContractSpecialConditionAction } from "actions";

import { ConfirmDialog } from "components/ConfirmDialog";
import { Label } from "components/Form/FormikControls/Label";
import { StyledTextAreaAutoSize } from "components/Form/FormikControls/layout";
import { Row } from "components/Layout";
import { ReactSelect } from "components/SearchableSelector";
import { BoldText, Text } from "components/Text";
import {
  MultiButton,
  SlimButton,
  SlimSecondaryButton,
} from "components/Button";

import {
  EContractSpecialConditionCategory,
  EContractSpecialConditionCategoryLabel,
} from "constants/eContractAgreement";
import { FaIcon } from "components/AgGridButtonIcon/agGridButtonIcon";

const categoryOptions = Object.values(EContractSpecialConditionCategory).map(
  category => ({
    value: category,
    label: EContractSpecialConditionCategoryLabel[category],
  }),
);

interface SpecialConditionsEditorProps {
  agreementId: string;
  conditions: EContractSpecialCondition[];
  readOnly?: boolean;
  onFormActiveChange?: (isActive: boolean) => void;
}

interface ConditionFormProps {
  agreementId: string;
  onClose: () => void;
  condition?: EContractSpecialCondition;
}

const ConditionForm = ({
  agreementId,
  onClose,
  condition,
}: ConditionFormProps): React.JSX.Element => {
  const dispatch = useDispatch();
  const [category, setCategory] = useState(
    categoryOptions.find(o => o.value === condition?.category) ??
      categoryOptions[0],
  );
  const [description, setDescription] = useState(condition?.description ?? "");

  const onSave = () => {
    if (!description.trim()) {
      return;
    }
    if (condition) {
      dispatch(
        EContractSpecialConditionAction.update({
          id: condition.id,
          category: category.value,
          description: description.trim(),
        }),
      );
    } else {
      dispatch(
        EContractSpecialConditionAction.create({
          agreementId,
          category: category.value,
          description: description.trim(),
        }),
      );
    }
    onClose();
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Label required>Category</Label>
        <ReactSelect
          options={categoryOptions}
          value={category}
          onChange={setCategory}
          menuPortalTarget={document.body}
        />
      </Grid>
      <Grid item xs={12}>
        <Label required>Description</Label>
        <StyledTextAreaAutoSize
          fullWidth
          placeholder="Description..."
          value={description}
          onChange={e => setDescription(e.target.value)}
          minRows={3}
        />
      </Grid>
      <Grid item xs={12} container justifyContent="flex-end">
        <Row gridGap={1}>
          <SlimSecondaryButton type="button" onClick={onClose}>
            Cancel
          </SlimSecondaryButton>
          <SlimButton
            type="button"
            onClick={onSave}
            disabled={!description.trim()}
          >
            {condition ? "Save" : "Add"}
          </SlimButton>
        </Row>
      </Grid>
    </Grid>
  );
};

export const EContractSpecialConditionsEditor = ({
  agreementId,
  conditions,
  readOnly = false,
  onFormActiveChange,
}: SpecialConditionsEditorProps): React.JSX.Element => {
  const dispatch = useDispatch();
  const [showForm, setShowForm] = useState(false);
  const [editingConditionId, setEditingConditionId] = useState<string | null>(
    null,
  );
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const openForm = () => {
    setShowForm(true);
    onFormActiveChange?.(true);
  };

  const closeForm = () => {
    setShowForm(false);
    onFormActiveChange?.(false);
  };

  const openEdit = (conditionId: string) => {
    setEditingConditionId(conditionId);
    onFormActiveChange?.(true);
  };

  const closeEdit = () => {
    setEditingConditionId(null);
    onFormActiveChange?.(false);
  };

  const onDelete = () => {
    if (confirmDeleteId) {
      dispatch(EContractSpecialConditionAction.delete(confirmDeleteId));
      setConfirmDeleteId(null);
    }
  };

  return (
    <Grid container spacing={1}>
      {conditions.length === 0 && !showForm && (
        <Grid item xs={12}>
          <Text italic>No special conditions added.</Text>
        </Grid>
      )}

      {conditions.map((condition: EContractSpecialCondition, index: number) => (
        <Grid item xs={12} key={condition.id}>
          {index > 0 && !readOnly && <Divider />}
          {editingConditionId === condition.id ? (
            <ConditionForm
              agreementId={agreementId}
              condition={condition}
              onClose={closeEdit}
            />
          ) : (
            <>
              <Row fullWidth paddingVertical={1}>
                <Grid item xs={9} md={10}>
                  <BoldText>
                    {EContractSpecialConditionCategoryLabel[condition.category]}
                  </BoldText>
                </Grid>
                <Grid
                  item
                  xs={3}
                  md={2}
                  container
                  alignContent="center"
                  justifyContent="flex-end"
                >
                  {!readOnly && (
                    <>
                      <MultiButton
                        ButtonComponent={SlimSecondaryButton}
                        buttons={[
                          {
                            title: "Edit",
                            icon: faPencil,
                            onClick: () => openEdit(condition.id),
                          },
                          {
                            title: "Delete",
                            icon: faTrash,
                            onClick: () => setConfirmDeleteId(condition.id),
                          },
                        ]}
                      />
                    </>
                  )}
                </Grid>
              </Row>
              <Row gridGap={1} alignCenter>
                <Grid container item xs={11} md={11}>
                  <Text>{condition.description}</Text>
                </Grid>
              </Row>
            </>
          )}
        </Grid>
      ))}

      {!readOnly &&
        !editingConditionId &&
        (showForm ? (
          <Grid item xs={12}>
            <ConditionForm agreementId={agreementId} onClose={closeForm} />
          </Grid>
        ) : (
          <Grid item xs={12} container justifyContent="flex-end">
            <SlimButton type="button" onClick={openForm} fullWidth={false}>
              <FaIcon icon={faPlus} /> Add Condition
            </SlimButton>
          </Grid>
        ))}
      <ConfirmDialog
        buttonMessage="Delete"
        message="Deleting this Condition will remove it from future drafts of this eContract. Are you sure you want to delete this Condition?"
        isOpen={!!confirmDeleteId}
        onCancel={() => setConfirmDeleteId(null)}
        onDelete={onDelete}
        title="Delete this special condition?"
      />
    </Grid>
  );
};
